<script>
  import { onMount } from 'svelte';
  import { user } from '../stores/authStore.js';
  import { navigate } from 'svelte-routing';

  export let title = '';
  export let category = '';   // active category name (slug)
  export let slug = '';       // active doc slug
  export let requiresAuth = false;

  const SUPPORT_EMAIL = 'joe@buildzilla.co';

  let categories = [];
  let openState = {};         // category name -> bool (explicit user toggles only)
  let sessionChecked = false;

  // A category is open if the user toggled it; otherwise the active category
  // is open by default. This keeps the active section expanded on navigation
  // without a read/write feedback loop on the toggle state.
  const isOpen = (name, state, active) => (name in state ? state[name] : name === active);

  // Redirect to login only for auth-gated docs, once we know the session state.
  $: if (requiresAuth && !$user && sessionChecked) {
    navigate('/login');
  }

  onMount(async () => {
    try {
      const response = await fetch('/api/user', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) user.set(data.user);
      }
    } catch (_e) { /* not logged in — fine */ }
    sessionChecked = true;
    await loadNav();
  });

  async function loadNav() {
    try {
      const res = await fetch('/content/help-manifest.json');
      if (!res.ok) return;
      const manifest = await res.json();
      categories = Object.entries(manifest.categories)
        .map(([name, data]) => ({
          name,
          label: data.label || name,
          files: (data.files || []).slice().sort((a, b) => a.position - b.position),
          position: data.position ?? 999,
        }))
        .sort((a, b) => a.position - b.position);
    } catch (e) {
      console.error('Error loading help nav:', e);
    }
  }

  $: supportSubject = title
    ? `Buildzilla support — ${title}`
    : 'Buildzilla support';
  $: SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(supportSubject)}`;

  function toggle(name) {
    openState = { ...openState, [name]: !isOpen(name, openState, category) };
  }
</script>

<div class="min-h-screen bg-gray-50">
  <div class="flex">
    <!-- Sidebar: primary navigation -->
    <aside class="w-72 shrink-0 bg-white border-r border-gray-200 h-screen sticky top-0 overflow-y-auto">
      <div class="p-4">
        <h2 class="text-xl font-bold text-gray-800 mb-3">Help Center</h2>

        <a
          href="/dashboard"
          class="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors font-medium mb-3"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to App
        </a>

        <!-- Reaching a human has to be possible from inside the app, not only
             from the articles. Sits above the nav so it is findable when the
             docs have not answered the question. -->
        <a
          href={SUPPORT_MAILTO}
          class="flex items-center justify-center gap-2 w-full mb-4 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors no-underline"
          style="color: white !important;"
        >
          <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
          Contact Support
        </a>

        <nav class="space-y-0.5">
          {#each categories as cat}
            <div>
              <button
                type="button"
                on:click={() => toggle(cat.name)}
                class="w-full flex items-center justify-between px-2 py-2 rounded-md text-left text-sm font-semibold transition-colors
                  {category === cat.name ? 'text-blue-700 bg-blue-50' : 'text-gray-700 hover:bg-gray-100'}"
                aria-expanded={isOpen(cat.name, openState, category) ? 'true' : 'false'}
              >
                <span>{cat.label}</span>
                <svg
                  class="w-4 h-4 shrink-0 text-gray-400 transition-transform duration-150 {isOpen(cat.name, openState, category) ? 'rotate-90' : ''}"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>

              {#if isOpen(cat.name, openState, category)}
                <ul class="mt-0.5 mb-1 ml-2 border-l border-gray-200 space-y-0.5">
                  {#each cat.files as doc}
                    <li>
                      <a
                        href="/help/{cat.name}/{doc.slug}"
                        class="block pl-3 pr-2 py-1.5 -ml-px border-l-2 text-sm transition-colors
                          {category === cat.name && slug === doc.slug
                            ? 'border-blue-600 text-blue-700 font-medium bg-blue-50/60'
                            : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'}"
                      >
                        {doc.title}
                      </a>
                    </li>
                  {/each}
                </ul>
              {/if}
            </div>
          {/each}
        </nav>
      </div>
    </aside>

    <!-- Main content -->
    <div class="flex-1 min-w-0 overflow-y-auto">
      <div class="max-w-4xl mx-auto p-8">
        {#if title}
          <h1 class="text-3xl font-bold text-gray-900 mb-6">{title}</h1>
        {/if}
        <div class="prose prose-lg max-w-none">
          <slot />
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  :global(.prose) { @apply text-gray-700; }
  :global(.prose h1) { @apply text-2xl font-bold text-gray-900 mt-8 mb-4; }
  :global(.prose h2) { @apply text-xl font-semibold text-gray-800 mt-6 mb-3; }
  :global(.prose h3) { @apply text-lg font-medium text-gray-800 mt-4 mb-2; }
  :global(.prose p) { @apply mb-4 leading-relaxed; }
  :global(.prose ul) { @apply mb-4; }
  :global(.prose li) { @apply mb-1; }
  :global(.prose code) { @apply bg-gray-100 px-2 py-1 rounded text-sm; }
  :global(.prose pre) { @apply bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4; }
  :global(.prose blockquote) { @apply border-l-4 border-blue-500 pl-4 italic text-gray-600; }
  :global(.prose img) { @apply rounded-lg shadow-md mb-4; }
</style>
